import React, { useState, useEffect, useContext } from 'react';
import {
    Modal, Box, SpaceBetween, Button, FormField, Textarea, Alert, Tiles
} from '@amzn/awsui-components-react';
import { GlobalAppContext } from 'src/components/App';
import { ApprovalType, ApprovalAction, ProcessApprovalRequest } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { MultiAllocationSheetValidationData } from 'src/models/multidimensional-allocation/MultiAllocationSheetDetails';
import ErrorUtils from 'src/utils/ErrorUtils';
import {IMPACT_NOTICE_MESSAGES} from "src/components/multidimensional-allocation/common/Constants";

export interface ProcessApprovalModalProps {
    /** The entity ID to process approval for */
    entityId: string;
    /** Type of entity being approved */
    approvalType: ApprovalType;
    /** Validation data for the entity */
    validationData?: MultiAllocationSheetValidationData;
    /** Current modal mode */
    mode: 'closed' | 'submit' | 'approval';
    /** Callback to change modal mode */
    onModeChange: (mode: 'closed' | 'submit' | 'approval') => void;
    /** Callback function called after successful operation */
    onSuccess?: () => void;
}

/**
 * Modal component for processing approval actions on multidimensional allocation entities.
 * Supports both submission for review and approval/rejection workflows with validation feedback.
 *
 * @returns React functional component rendering approval modal dialogs
 */
export const ProcessApprovalModal: React.FC<ProcessApprovalModalProps> = ({
    entityId, approvalType, validationData, mode, onModeChange, onSuccess 
}) => {
        const service = useContext(GlobalAppContext).services.multidimensionalAllocationService;

        // Form state
        const [submitComment, setSubmitComment] = useState('');
        const [approvalComment, setApprovalComment] = useState('');
        const [approvalDecision, setApprovalDecision] = useState<ApprovalAction.APPROVE | ApprovalAction.REJECT>(ApprovalAction.APPROVE);
        
        // Loading states
        const [submitLoading, setSubmitLoading] = useState(false);
        const [approvalLoading, setApprovalLoading] = useState(false);
        
        // Shared error state
        const [error, setError] = useState<string>('');

        // Set correct default when approval modal is opened
        useEffect(() => {
            if (mode === 'approval') {
                const hasErrors = validationData?.validationErrors && validationData.validationErrors.length > 0;
                setApprovalDecision(hasErrors ? ApprovalAction.REJECT : ApprovalAction.APPROVE);
                setError(''); // Clear errors when opening
            } else if (mode === 'submit') {
                setError(''); // Clear errors when opening
            }
        }, [mode, validationData]);

        // Generic handler for all approval actions
        const handleApprovalAction = async (
            action: ApprovalAction,
            comment: string,
            setLoading: (loading: boolean) => void,
            hideModal: () => void,
            clearComment: () => void
        ) => {
            try {
                setLoading(true);
                setError(''); // Clear any previous errors
                const processApprovalRequest: ProcessApprovalRequest = {
                    entityId: entityId,
                    approvalData: {
                        comment: comment
                    }
                };
                
                await service.processMultiAllocationApproval(
                    approvalType, 
                    action, 
                    processApprovalRequest
                );
                
                hideModal();
                clearComment();
                onSuccess?.();
            } catch (err) {
                setError(ErrorUtils.getMessage(err));
            } finally {
                setLoading(false);
            }
        };


        const hasValidationErrors = validationData?.validationErrors && validationData.validationErrors.length > 0;

        return (
            <>
                {/* Submit for Review Modal */}
                <Modal
                    onDismiss={() => {
                        if (!submitLoading) {
                            onModeChange('closed');
                            setSubmitComment('');
                            setError(''); // Clear errors on dismiss
                        }
                    }}
                    visible={mode === 'submit'}
                    closeAriaLabel="Close modal"
                    size="medium"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button 
                                    variant="link" 
                                    onClick={() => {
                                        onModeChange('closed');
                                        setSubmitComment('');
                                        setError(''); // Clear errors on cancel
                                    }}
                                    disabled={submitLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => handleApprovalAction(
                                        ApprovalAction.SUBMIT,
                                        submitComment,
                                        setSubmitLoading,
                                        () => onModeChange('closed'),
                                        () => setSubmitComment('')
                                    )}
                                    loading={submitLoading}
                                >
                                    Submit
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                    header="Submit for Review"
                >
                    <SpaceBetween direction="vertical" size="m">
                        <FormField label="Comments (Optional)">
                            <Textarea
                                value={submitComment}
                                onChange={({detail}) => setSubmitComment(detail.value)}
                                placeholder="Add any comments about this submission..."
                                rows={4}
                                disabled={submitLoading}
                            />
                        </FormField>
                        
                        {error && (
                            <Alert type="error" dismissible header="Submit failed" onDismiss={() => setError('')}>
                                {error}
                            </Alert>
                        )}
                    </SpaceBetween>
                </Modal>

                {/* Approval Decision Modal */}
                <Modal
                    onDismiss={() => {
                        if (!approvalLoading) {
                            onModeChange('closed');
                            setApprovalComment('');
                            setError(''); // Clear errors on dismiss
                        }
                    }}
                    visible={mode === 'approval'}
                    closeAriaLabel="Close modal"
                    size="medium"
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button 
                                    variant="link" 
                                    onClick={() => {
                                        onModeChange('closed');
                                        setApprovalComment('');
                                        setError(''); // Clear errors on cancel
                                    }}
                                    disabled={approvalLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => handleApprovalAction(
                                        approvalDecision,
                                        approvalComment,
                                        setApprovalLoading,
                                        () => onModeChange('closed'),
                                        () => setApprovalComment('')
                                    )}
                                    loading={approvalLoading}
                                >
                                    Confirm
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                    header="Approval decision"
                >
                    <SpaceBetween direction="vertical" size="m">
                        <FormField label="Change note">
                            <Textarea
                                value={approvalComment}
                                onChange={({detail}) => setApprovalComment(detail.value)}
                                placeholder="Please add justification for the action"
                                rows={4}
                                disabled={approvalLoading}
                            />
                        </FormField>
                        
                        <FormField label="Decision">
                            <Tiles
                                onChange={({ detail }) => setApprovalDecision(detail.value as ApprovalAction.APPROVE | ApprovalAction.REJECT)}
                                value={approvalDecision}
                                items={[
                                    {
                                        value: ApprovalAction.APPROVE,
                                        label: "Approve",
                                        disabled: hasValidationErrors
                                    },
                                    {
                                        value: ApprovalAction.REJECT,
                                        label: "Reject",
                                        disabled: false
                                    }
                                ]}
                            />
                        </FormField>

                        {error && (
                            <Alert type="error" dismissible header="Approval failed" onDismiss={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        <Alert type="warning" header="Impact Notice">
                            <p>{IMPACT_NOTICE_MESSAGES.get(approvalType)?.get(approvalDecision)}</p>
                        </Alert>

                    </SpaceBetween>
                </Modal>
            </>
        );
};
